import { Currency, Token } from '@pancakeswap-libs/sdk'
import { useCallback, useMemo, useState } from 'react'
import { WrappedTokenInfo } from '../state/lists/hooks'
import useHttpLocations from './useHttpLocations'

interface EthereumProvider {
  request: (args: { method: string; params?: unknown }) => Promise<unknown>
}

// EIP-747 `wallet_watchAsset` - prompts the connected wallet to add an ERC20 token
// to its asset list. Only usable for ERC20 tokens (not the native currency) and
// only when an injected provider (eg. MetaMask) is present.
export default function useAddTokenToWallet(
  currencyToAdd: Currency | undefined
): { addToken: () => void; success: boolean | undefined; canAdd: boolean } {
  const token: Token | undefined = currencyToAdd instanceof Token ? currencyToAdd : undefined

  const rawLogoUrl = currencyToAdd instanceof WrappedTokenInfo ? currencyToAdd.logoURI : undefined
  const logoLocations = useHttpLocations(rawLogoUrl)

  const [success, setSuccess] = useState<boolean | undefined>()

  const ethereum = typeof window !== 'undefined' ? ((window as any).ethereum as EthereumProvider | undefined) : undefined
  const canAdd = Boolean(ethereum && token)

  const addToken = useCallback(() => {
    if (!ethereum || !token) {
      setSuccess(false)
      return
    }

    ethereum
      .request({
        method: 'wallet_watchAsset',
        params: {
          type: 'ERC20',
          options: {
            address: token.address,
            symbol: token.symbol,
            decimals: token.decimals,
            image: logoLocations[0],
          },
        },
      })
      .then((result) => setSuccess(Boolean(result)))
      .catch(() => setSuccess(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ethereum, token, logoLocations[0]])

  return useMemo(() => ({ addToken, success, canAdd }), [addToken, success, canAdd])
}
